import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { eDocumentType } from '../../enums';
import { Document } from '../../interfaces/documents/document';
import { Base64Service } from '../../services/base-64/base64-service';
import { DocumentsRepositoryService } from '../../services/document/documents-repository.service';
import { DocumentBinary } from '../../interfaces/documents/document-binary';
import { DocumentCreatedEvent } from '../../interfaces/document-created-event';
import { TranslocoService } from '@jsverse/transloco';

type AcceptMap = { [K in eDocumentType]: string };

export interface HandleUploadLabels {
  fileAttached: string;
  attachDocument: string;
  expected: string;
  current: string;
  upload: string;
  expectedGot: string;
  failedToCreate: string;
  documentUploaded: string;
  failedToUpload: string;
  failedToRead: string;
  close: string;
}

@Component({
    selector: 'lib-handle-upload',
    templateUrl: './handle-upload.component.html',
    styleUrls: ['./handle-upload.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HandleUploadComponent {
  private readonly transloco = inject(TranslocoService);
  @Input() label: string | null = null;
  @Input() currentDocument: Document | null = null;
  @Input() expectedType: eDocumentType = eDocumentType.pdf;
  @Input() disabled = false;
  @Input() showDownload = false;
  @Input() acceptOverride: string | null = null;
  @Input() isRequired = false;
  @Input() showSnackBar: boolean = true;
  private _labels?: Partial<HandleUploadLabels>;
  @Input() set labels(value: Partial<HandleUploadLabels> | null | undefined) { this._labels = value ?? undefined; }
  /** Caller-provided labels override the translated defaults. */
  get labels(): HandleUploadLabels {
    const defaults: HandleUploadLabels = {
      fileAttached: this.transloco.translate('handleUpload.fileAttached'),
      attachDocument: this.transloco.translate('handleUpload.attachDocument'),
      expected: this.transloco.translate('handleUpload.expected'),
      current: this.transloco.translate('handleUpload.current'),
      upload: this.transloco.translate('common.buttons.upload'),
      expectedGot: this.transloco.translate('handleUpload.expectedGot'),
      failedToCreate: this.transloco.translate('handleUpload.failedToCreate'),
      documentUploaded: this.transloco.translate('handleUpload.documentUploaded'),
      failedToUpload: this.transloco.translate('handleUpload.failedToUpload'),
      failedToRead: this.transloco.translate('handleUpload.failedToRead'),
      close: this.transloco.translate('common.buttons.close'),
    };
    return { ...defaults, ...this._labels } as HandleUploadLabels;
  }

  @Output() documentCreated = new EventEmitter<DocumentCreatedEvent>();
  @Output() cleared = new EventEmitter<void>();
  @Output() downloadRequested = new EventEmitter<void>();

  uploading = false;

  private readonly acceptByType: AcceptMap = {
    [eDocumentType.pdf]:  '.pdf',
    [eDocumentType.text]: '.txt',
    [eDocumentType.xml]:  '.xml',
    [eDocumentType.html]: '.html',
    [eDocumentType.json]: '.json',
    [eDocumentType.csv]:  '.csv'
  };

  constructor(
    private base64: Base64Service,
    private docsRepo: DocumentsRepositoryService,
    private snackBar: MatSnackBar
  ) {}

  get accept(): string {
    if (this.acceptOverride) return this.acceptOverride;
    return this.acceptByType[this.expectedType] ?? '.pdf';
  }

  get hasExisting(): boolean {
    return !!this.currentDocument?.id;
  }

  onPick(fileInput: HTMLInputElement) {
    if (this.disabled) return;
    fileInput.click();
  }

  onClear() {
    if (this.disabled) return;
    this.cleared.emit();
  }

  onDownload() {
    this.downloadRequested.emit();
  }

  onFileSelected(evt: Event) {
    if (this.disabled) return;

    const input = evt.currentTarget as HTMLInputElement;
    const file = input?.files?.[0] ?? null;
    if (!file) return;

    const actualType = this.mapMimeToEnum(file.type);
    if (actualType !== this.expectedType) {
      const msg = this.labels.expectedGot
        .replace('{{expected}}', this.expectedType.toUpperCase())
        .replace('{{actual}}', actualType.toUpperCase());
      this.openSnackBar(msg);
      input.value = '';
      return;
    }

    this.uploading = true;

    this.base64.convertToBase64(file).subscribe({
      next: (b64) => {
        const payload: DocumentBinary = {
          fileName: file.name,
          documentTypeID: this.expectedType, // "PDF" | "JSON" etc, matches your API
          base64: b64
        };

        this.docsRepo.createDocumentBinary(payload).subscribe({
          next: (res) => {
            const id = res.resource?.id ?? '';
            const storagePath = res.resource?.storagePath;
            const storageID = res.resource?.storageID
            if (!id) {
              this.openSnackBar(this.labels.failedToCreate);
            } else {
              this.openSnackBar(this.labels.documentUploaded);
              this.documentCreated.emit({
                id,
                fileName: file.name,
                documentTypeID: this.expectedType,
                storagePath,
                storageID
              });
            }
            this.uploading = false;
            input.value = '';
          },
          error: () => {
            this.uploading = false;
            input.value = '';
            this.openSnackBar(this.labels.failedToUpload);
          }
        });
      },
      error: () => {
        this.uploading = false;
        input.value = '';
        this.openSnackBar(this.labels.failedToRead);
      }
    });
  }

  /** Map browser MIME to your enum for validation (kept in sync with your previous switch). */
  private mapMimeToEnum(mime: string): eDocumentType {
    switch (mime) {
      case 'application/pdf': return eDocumentType.pdf;
      case 'text/plain': return eDocumentType.text;
      case 'application/xml':
      case 'text/xml': return eDocumentType.xml;
      case 'text/html': return eDocumentType.html;
      case 'application/json': return eDocumentType.json;
      case 'text/csv':
      case 'application/vnd.ms-excel': return eDocumentType.csv;
      default: return eDocumentType.pdf;
    }
  }

  private openSnackBar(message: string) {
    if (this.showSnackBar) {
      const config: MatSnackBarConfig = { duration: 4000 };
      this.snackBar.open(message, this.labels.close, config);
    }
  }
}